import { Injectable } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CookieConsentBottomSheetComponent } from '../../../shared/components/cookie-consent-bottom-sheet/cookie-consent-bottom-sheet.component';

const STORAGE_KEY = 'cookie_consent';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  [key: string]: boolean;
}

export type ConsentStatus = 'accepted' | 'rejected' | 'customized' | null;

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  constructor(private bottomSheet: MatBottomSheet) {}

  /** Returns current consent status from storage, or null if not set. */
  getConsentStatus(): ConsentStatus {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      const data = JSON.parse(raw);
      return data.status ?? null;
    } catch {
      return null;
    }
  }

  /** Returns saved cookie preferences (for customized consent). */
  getPreferences(): CookiePreferences | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      const data = JSON.parse(raw);
      return data.preferences ?? null;
    } catch {
      return null;
    }
  }

  /** Whether the user has already made a choice (accept / reject / customize). */
  hasUserResponded(): boolean {
    return this.getConsentStatus() != null;
  }

  /** Persist consent and close the sheet. */
  accept(): void {
    this.saveConsent('accepted', {
      necessary: true,
      analytics: true,
      marketing: true
    });
  }

  /** Persist rejection (only necessary). */
  reject(): void {
    this.saveConsent('rejected', {
      necessary: true,
      analytics: false,
      marketing: false
    });
  }

  /** Persist customized preferences. */
  saveCustomized(preferences: CookiePreferences): void {
    this.saveConsent('customized', preferences);
  }

  private saveConsent(status: ConsentStatus, preferences: CookiePreferences): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      status,
      preferences,
      timestamp: new Date().toISOString()
    }));
  }

  /** Opens the cookie consent bottom sheet if the user has not responded yet. */
  showIfNeeded(): void {
    if (this.hasUserResponded()) return;
    this.bottomSheet.open(CookieConsentBottomSheetComponent, {
      disableClose: true,
      panelClass: 'cookie-consent-bottom-sheet'
    });
  }

  /** Opens the cookie consent bottom sheet (e.g. from "Cookie settings" link). */
  show(): void {
    this.bottomSheet.open(CookieConsentBottomSheetComponent, {
      disableClose: false,
      panelClass: 'cookie-consent-bottom-sheet'
    });
  }
}
